import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Formik,
  FastField,
  Form,
} from 'formik';
import { forEachSeries } from 'p-iteration';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import { ReactComponent as CheckIcon } from '../../../../../../assets/icons/v2/check-circle.svg';
import { ReactComponent as CancelIcon } from '../../../../../../assets/icons/v2/cross-cancel.svg';
import { ReactComponent as GroupIcon } from '../../../../../../assets/icons/group-icon.svg';

import UserContext from '../../../../../../context/UserContext';
import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import WorkoutContext from '../../../../../context/WorkoutContext';
import WorkoutAssignment from '../../../../../../Model/WorkoutAssignment';
import User from '../../../../../../Model/User';
import ChallengePropagationDialog from '../../../../../components/ChallengePropagationDialog';
import useChallengeSaveConfirmation from '../../../../../hooks/useChallengeSaveConfirmation';
import InputLabel from '../../../../../../components/v2/InputLabel';
import {
  HeaderRow,
  Title,
} from '../../../../../../components/v2/Header';
import FormikInput from '../../../../../../components/v2/FormikInput';
import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import {
  SectionContainer,
  SectionHeaderContainer,
  SectionTitle,
  SectionCompartment,
  SectionFooterContainer,
} from '../../../../../../components/v2/Section';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import Program, {
  DifficultyLevel,
} from '../../../../../../Model/Program';
import Workout from '../../../../../../Model/Workout';
import WorkoutEditorModal from '../../../../../components/WorkoutEditorModal';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import useToast from '../../../../../hooks/useToast';
import { InfoTag } from '../../../../../../components/Tags';
import RadioButtonGroup from '../../../../../components/RadioButtonGroup';
import {
  ActionType,
  getWorkoutEditorMobileUrl,
} from '../../../Workouts/utils';
import {
  formatWorkoutDay,
  toWorkoutRefs,
  isUnownedPublicProgram,
} from '../../utils';
import useLogger from '../../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../../utils/log';
import UnaffectedUserList from './components/ProgramEditUnaffectedUser';
import {
  WorkoutDaysSelector,
  WorkoutPreview,
} from './components';
import {
  Container,
  FormSection,
  Label,
  StyledFormRowScale,
  EquipmentContainer,
  FieldContent,
  StyledSaveIcon,
  WorkoutDaysSection,
  ContainerContent,
  UserListContainer,
  UnaffectedClientListButtonWrapper,
  StyledAlert,
  ProgramIdSection,
  ProgramIdValue,
} from './styles';
import { fieldName } from './formFields';
import {
  initialValues as startValues,
  validationSchema,
  initialWorkoutDayValues,
} from './validation';
import texts from './texts';

const difficultyLevelOptions = Object.values(DifficultyLevel).map((difficultyLevel) => ({
  label: texts.difficultyLevel[difficultyLevel],
  value: difficultyLevel,
}));

const ProgramEditor = ({
  programDoc,
  onClose,
  isEditView,
}) => {
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { isAdmin } = useSessionStore();
  const { userId: coachId } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);

  const { workouts, filteredWorkouts } = useContext(WorkoutContext);

  const [isLoading, setIsLoading] = useState(false);
  const [mobileUrl, setMobileUrl] = useState('');
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [unaffectedClients, setUnaffectedClients] = useState();
  /**
   * The workouts the program refers to, read by id. The workouts list only holds the coach's own and
   * the client-facing public ones, so a public program's private workouts are missing from it. Null
   * while they load, or when they could not be loaded: saving then is blocked, since every day not
   * found would be saved as a rest day.
   */
  const [programWorkouts, setProgramWorkouts] = useState(programDoc ? null : []);
  // Set when the program's workouts could not be read, so the loading overlay clears and the coach can cancel.
  const [hasProgramWorkoutsLoadFailed, setHasProgramWorkoutsLoadFailed] = useState(false);

  const bottomContainerRef = useRef(null);
  const formikRef = useRef(null);

  const { logCoachingActivity } = useLogger();

  useEffect(() => {
    if (!programDoc) {
      setProgramWorkouts([]);
      return;
    }
    setProgramWorkouts(null);
    setHasProgramWorkoutsLoadFailed(false);
    const workoutIds = [...new Set(programDoc.workouts.map(({ workoutRef }) => workoutRef).filter(Boolean))];
    const loadProgramWorkouts = async () => {
      try {
        const loadedWorkouts = workoutIds.length ? await Workout.getWorkoutsByIds(workoutIds) : [];
        if (isComponentMountedRef.current) {
          setProgramWorkouts(loadedWorkouts);
        }
      } catch (error) {
        Sentry.captureException(error);
        if (isComponentMountedRef.current) {
          setHasProgramWorkoutsLoadFailed(true);
          showToast(texts.programWorkoutsLoadFailed, { error: true });
        }
      }
    };
    loadProgramWorkouts();
  }, [
    programDoc,
    isComponentMountedRef,
    showToast,
  ]);

  useEffect(() => {
    if (!!unaffectedClients && bottomContainerRef.current) {
      bottomContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [unaffectedClients]);

  const onWorkoutAssignmentClick = useCallback((workoutAssignment) => {
    setMobileUrl(getWorkoutEditorMobileUrl(coachId, workoutAssignment, false));
  }, [coachId]);

  const onEditWorkoutClick = useCallback((workoutId, dayIndex) => {
    // The day selector offers editing only for workouts in this list (the coach's own).
    const workout = workouts.find(({ id }) => id === workoutId);
    /**
     * Snapshot the current form days so the editor can detect same-program sharing against what
     * the coach currently sees (which may differ from the persisted program).
     */
    const programWorkoutDays = formikRef.current?.values?.[fieldName.WORKOUT_DAYS] || [];
    setSelectedWorkout({ workout, dayIndex, programWorkoutDays });
  }, [workouts]);

  const onSaveWorkout = useCallback(async (newWorkoutId, dayIndex) => {
    /**
     * If the edit forked a private copy of a shared workout, reflect it on this day in the form
     * only. The re-point is persisted to the program when the coach explicitly saves the program
     */
    if (newWorkoutId && formikRef.current && dayIndex != null) {
      try {
        const newWorkoutDoc = await Workout.getWorkoutById(newWorkoutId);
        formikRef.current.setFieldValue(`${fieldName.WORKOUT_DAYS}[${dayIndex}]`, formatWorkoutDay(newWorkoutDoc));
      } catch (error) {
        // The forked workout was created, but we couldn't load it to show on the day. Report it
        // and tell the coach, rather than leaving the day silently showing the old workout.
        Sentry.captureException(error);
        showToast(texts.workoutForkLoadFailed, { error: true });
        return;
      }
    }
    setSelectedWorkout(null);
    // Force render the preview mobile view if it's open, to reflect any updates done on the workout
    if (mobileUrl) {
      setPreviewKey((prev) => prev + 1);
    }
  }, [mobileUrl, showToast]);

  /**
   * Initializes the workoutList with properly formatted workouts.
   *
   * If the workout reference is null, it is considered as the initial workout value (Rest & Recovery).
   * If it is not null, the workout is looked up in the workouts list, then in the program's own workouts
   * (which hold the ones the list leaves out, such as a public program's private or archived workouts).
   */
  const getFormattedWorkouts = useCallback((workoutDays) => {
    const assignedWorkouts = [];
    workoutDays.forEach((workoutDay) => {
      if (!workoutDay.workoutRef) {
        assignedWorkouts.push(initialWorkoutDayValues);
      } else {
        const findWorkout = ({ id }) => id === workoutDay.workoutRef;
        const workoutDetails = filteredWorkouts.find(findWorkout) || programWorkouts?.find(findWorkout);
        // A workout whose document no longer exists is considered as the initial workout (Rest & Recovery).
        const assignedWorkout = workoutDetails
          ? formatWorkoutDay(workoutDetails)
          : initialWorkoutDayValues;
        assignedWorkouts.push(assignedWorkout);
      }
    });
    return assignedWorkouts;
  }, [
    filteredWorkouts,
    programWorkouts,
  ]);

  const initialValues = useMemo(() => (
    programDoc
      ? {
        ...programDoc.data,
        [fieldName.WORKOUT_DAYS]: getFormattedWorkouts(programDoc.workouts),
        [fieldName.EQUIPMENT]: programDoc.equipment,
      }
      : startValues
  ), [
    programDoc,
    getFormattedWorkouts,
  ]);

  const isLoadingProgramWorkouts = !programWorkouts && !hasProgramWorkoutsLoadFailed;

  const saveProgram = useCallback(async (values) => {
    setIsLoading(true);
    const isPublicTracksCoach = !!coachDoc?.isPublicTracksCoach;
    const isProgramCopy = isUnownedPublicProgram(programDoc, coachDoc);
    const programData = {
      name: values[fieldName.NAME],
      workouts: toWorkoutRefs(values[fieldName.WORKOUT_DAYS]),
      ...(!!values[fieldName.DIFFICULTY] && { difficulty: values[fieldName.DIFFICULTY] }),
      equipment: values[fieldName.EQUIPMENT],
      ...(!programDoc || isProgramCopy
        ? {
          coach: coachId,
          isPublic: isPublicTracksCoach,
          isArchived: false,
          createdAt: new Date().getTime(),
        }
        : {
          isPublic: isPublicTracksCoach,
          updatedAt: new Date().getTime(),
        }),
    };
    // If a non-public-tracks coach edits a PUBLIC program, create a copy instead of updating the original
    if (isProgramCopy) {
      const program = await Program.addDoc({
        ...programData,
        originalProgram: programDoc.id,
      });
      logCoachingActivity(CoachingActivity.CREATED_PROGRAM, { programId: program.id });
    } else if (programDoc) {
      // For non-PUBLIC programs, update as normal
      await programDoc.updateFields(programData);
      logCoachingActivity(CoachingActivity.UPDATED_PROGRAM, { programId: programDoc.id });
    } else {
      // Creating a new program
      const program = await Program.addDoc(programData);
      logCoachingActivity(CoachingActivity.CREATED_PROGRAM, { programId: program.id });
    }
    if (isComponentMountedRef.current) {
      showToast(texts.programSaved);
      setIsLoading(false);
      onClose();
    }
  }, [
    showToast,
    isComponentMountedRef,
    coachId,
    coachDoc,
    programDoc,
    onClose,
    logCoachingActivity,
  ]);

  const {
    isChecking: isCheckingChallenge,
    pendingPropagation,
    requestSave,
    confirmSave,
    cancelSave,
  } = useChallengeSaveConfirmation(saveProgram);

  const handleSubmit = useCallback((values) => {
    // Only a program that is already saved and owned can be attached to a
    // challenge — a new program, or the private copy forked from a public one,
    // has no clients on it yet.
    const canBeChallengeProgram = !!programDoc && !isUnownedPublicProgram(programDoc, coachDoc);
    return requestSave(canBeChallengeProgram ? { programId: programDoc.id } : null, values);
  }, [
    programDoc,
    coachDoc,
    requestSave,
  ]);

  const getUnaffectedClients = useCallback(async () => {
    setIsLoading(true);
    const clientCache = {};
    const clientList = await WorkoutAssignment.getFutureAssignmentsByProgramId(programDoc.id);

    await forEachSeries(clientList.docs, async (assignment) => {
      if (!clientCache[assignment.user]) {
        const client = await User.getById(assignment.user);
        clientCache[assignment.user] = {
          id: client.id,
          startDate: assignment.startDate,
          endDate: assignment.endDate,
          userDoc: client,
        };
      } else {
        if (assignment.startDate < clientCache[assignment.user].startDate) {
          clientCache[assignment.user].startDate = assignment.startDate;
        }
        if (assignment.endDate > clientCache[assignment.user].endDate) {
          clientCache[assignment.user].endDate = assignment.endDate;
        }
      }
    });
    if (isComponentMountedRef.current) {
      setUnaffectedClients(Object.values(clientCache));
      setIsLoading(false);
    }
  }, [
    programDoc,
    isComponentMountedRef,
  ]);

  return (
    <>
      <Container>
        <HeaderRow>
          <Title>{isEditView ? texts.editProgramTitle : texts.addNewProgramTitle}</Title>
        </HeaderRow>
        <ContainerContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            innerRef={formikRef}
            enableReinitialize
          >
            {({
              values,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <SectionContainer>
                  <SectionHeaderContainer>
                    <SectionTitle>{isEditView ? texts.editProgramTitle : texts.addNewProgram}</SectionTitle>
                  </SectionHeaderContainer>
                  <SectionCompartment>
                    {isEditView && programDoc && isAdmin && (
                      <ProgramIdSection>
                        <InputLabel>{`${texts.programId}:`}</InputLabel>
                        <ProgramIdValue>{programDoc.id}</ProgramIdValue>
                      </ProgramIdSection>
                    )}
                    <FormSection>
                      <FormikInput
                        name={fieldName.NAME}
                        label={`${texts.field[fieldName.NAME]}:`}
                      />
                    </FormSection>
                    <WorkoutDaysSection>
                      <FieldContent>
                        <WorkoutDaysSelector
                          workoutDays={values[fieldName.WORKOUT_DAYS]}
                          onPreviewWorkout={onWorkoutAssignmentClick}
                          onEditWorkout={onEditWorkoutClick}
                          onClosePreview={() => setMobileUrl('')}
                        />
                      </FieldContent>
                    </WorkoutDaysSection>
                    <FormSection>
                      <FieldContent>
                        <Label>{`${texts.field[fieldName.DIFFICULTY]}:`}</Label>
                        <StyledFormRowScale>
                          <RadioButtonGroup
                            options={difficultyLevelOptions}
                            showStyledOptions={false}
                            selectedOption={values[fieldName.DIFFICULTY]}
                            onOptionChange={(value) => setFieldValue(fieldName.DIFFICULTY, value)}
                          />
                        </StyledFormRowScale>
                      </FieldContent>
                    </FormSection>
                    <FormSection>
                      <FastField name={fieldName.EQUIPMENT}>
                        {({ field }) => (
                          <FieldContent>
                            <Label>{`${texts.field[fieldName.EQUIPMENT]}:`}</Label>
                            {field.value.length > 0 ? (
                              <EquipmentContainer>
                                {field.value.map((equipment) => (
                                  <InfoTag>{equipment}</InfoTag>
                                ))}
                              </EquipmentContainer>
                            ) : (
                              <div>{texts.emptyValue}</div>
                            )}
                          </FieldContent>
                        )}
                      </FastField>
                    </FormSection>
                  </SectionCompartment>
                  <SectionFooterContainer>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      icon={isEditView ? <StyledSaveIcon /> : <CheckIcon />}
                      disabled={isSubmitting || !programWorkouts}
                    >
                      {isEditView ? texts.saveChanges : texts.addNewProgram}
                    </PrimaryButton>
                    <PrimaryButton
                      onClick={onClose}
                      icon={<CancelIcon />}
                      variant="info"
                      size="medium"
                      disabled={isSubmitting}
                    >
                      {texts.cancel}
                    </PrimaryButton>
                  </SectionFooterContainer>
                </SectionContainer>
              </Form>
            )}
          </Formik>
          {!!mobileUrl && (
            <WorkoutPreview key={previewKey} mobileUrl={mobileUrl} />
          )}
        </ContainerContent>
        {isEditView && !programDoc?.isPublic && (
          <UnaffectedClientListButtonWrapper>
            <Title>{texts.unaffectedUserList}</Title>
            <StyledAlert severity="warning">
              {format(texts.updateProgramAlert, { program: programDoc.name })}
            </StyledAlert>
            <PrimaryButton
              variant="light"
              size="medium"
              icon={<GroupIcon />}
              onClick={getUnaffectedClients}
              disabled={!!unaffectedClients}
            >
              {texts.showUnaffectedUsers}
            </PrimaryButton>
          </UnaffectedClientListButtonWrapper>
        )}
        {isEditView && !programDoc?.isPublic && !!unaffectedClients && (
          <UserListContainer>
            <UnaffectedUserList
              clientList={unaffectedClients}
            />
          </UserListContainer>
        )}
        {!!selectedWorkout && (
          <WorkoutEditorModal
            showModal={!!selectedWorkout}
            workout={selectedWorkout.workout}
            programDoc={programDoc}
            dayIndex={selectedWorkout.dayIndex}
            programWorkoutDays={selectedWorkout.programWorkoutDays}
            onSave={onSaveWorkout}
            onClose={() => setSelectedWorkout(null)}
            editorAction={ActionType.UPDATE}
          />
        )}
        <ChallengePropagationDialog
          propagation={pendingPropagation}
          title={texts.challengeUpdateTitle}
          content={texts.challengeUpdateContent}
          onConfirm={confirmSave}
          onCancel={cancelSave}
        />
        <LoadingOverlay isLoading={isLoading || isCheckingChallenge || isLoadingProgramWorkouts} />
      </Container>
      <div ref={bottomContainerRef} />
    </>
  );
};

ProgramEditor.propTypes = {
  programDoc: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  isEditView: PropTypes.bool,
};

ProgramEditor.defaultProps = {
  programDoc: null,
  isEditView: false,
};

export default ProgramEditor;
